/**
 * Starea de autentificare a widgetului, ca functii pure: fara DOM, fara importuri, fara retea.
 *
 * Invariant: auth.isAuth === true  =>  auth.state.phone nu e gol; altfel pasul de telefon trebuie
 * sa apara. Pana acum o pagina randata logat + telefon verificat sarea pasul de auth, deci
 * bwAuth.state nu se construia niciodata si Finalize pleca mereu cu phone:null. Cand sesiunea de
 * 120 min murea, store() raspundea 419 si retry-ul CSRF retrimitea acelasi payload gol - rezervare
 * pierduta (dollieskin, 2026-09-12).
 *
 * Re-auth-ul de la Finalize: serverul raspunde 419 {message, reason} DOAR bundle-urilor care trimit
 * `bwReauth` (poarta din AppointmentController / SessionlessReauthGate). Widgetul cere atunci o
 * singura data codul SMS, pastrand slotul si notitele, si retrimite rezervarea daca slotul e acelasi.
 */

export const hasPhone = (v) => String(v ?? '').trim() !== '';

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Completeaza auth.state din auth.user (telefon, email) fara sa schimbe identitatea obiectului
 * existent (controller.js il foloseste ca sharedState). Telefonul deja din state castiga: e cel
 * tastat / validat in widget.
 */
export function seedAuthState(auth, bwState) {
    if (!auth || !auth.isAuth) return auth;
    const user = isObj(auth.user) ? auth.user : {};
    const st = isObj(auth.state) ? auth.state : {};
    if (!hasPhone(st.phone) && hasPhone(user.phone)) st.phone = String(user.phone).trim();
    if (!st.email && user.email) st.email = user.email;
    auth.state = st;
    // Pe drumul de oaspete (sesiune moarta) randul primeste un nume real, nu 'Fara cont'
    // (Web/Appointment.php); cu utilizator logat serverul ia oricum numele din cont.
    if (bwState && !bwState.bookingName && user.name) bwState.bookingName = user.name;
    // Logat, dar fara telefon cunoscut: pasul de auth trebuie sa apara (controller.js -> booking-phone).
    if (!hasPhone(st.phone)) auth.isChecked = false;
    return auth;
}

/** Campurile de identitate ale POST-ului de la Finalize. */
export function authPayload(auth) {
    const st = (auth && isObj(auth.state)) ? auth.state : {};
    return {
        phone: hasPhone(st.phone) ? String(st.phone).trim() : null,
        // Tara aleasa in selectorul de langa telefon: serverul citeste sub ea un numar tastat fara
        // prefix ("757076424") si il scrie in forma salonului ("0757076424"). null = fara selector.
        phoneCountry: st.phoneCountry || null,
        noAccount: st.noAccount || false,
        isAuth: !!(auth && auth.isAuth),
        // Prezenta cheii ii spune serverului „bundle-ul asta poate re-dovedi telefonul”;
        // 1 = pe pagina asta a avut loc deja un re-auth.
        bwReauth: auth && auth.reauthUsed ? 1 : 0,
    };
}

/** Amprenta slotului ales: daca s-a schimbat intre 419 si revenirea pe confirm, NU retrimitem singuri. */
export function slotFingerprint(s) {
    const st = s || {};
    return JSON.stringify([
        st.selectedLocation ?? null,
        st.selectedEmployee ?? null,
        [...(st.selectedServices || [])],
        st.date ?? null,
        st.startAt ?? null,
        st.endAt ?? null,
        st.selectedClass ?? null,
        st.appointmentId ?? null,
        isObj(st.selectedSubscription) ? st.selectedSubscription.id : null,
    ]);
}

/**
 * Porneste re-auth-ul: isAuth, user si state raman (booking-phone se deschide precompletat).
 * bwResume e NE-enumerabil: persistState / storeWindowState serializeaza bwState la un login social
 * pornit din ecranul de re-auth, iar un bwResume restaurat dupa redirect ar retrimite rezervarea la
 * incarcarea paginii, fara nicio atingere. Reluarea automata tine doar in memoria paginii curente.
 */
export function beginReauth(auth, bwState, notes, reason) {
    auth.reauth = true;      // booking-phone arata textul de re-auth; authSms incearca restaurarea login-ului
    auth.reauthUsed = true;  // opritor de bucla: cel mult un re-auth pe incarcare de pagina
    // login_required pe un cont fara SMS (skipValidate): booking-phone trimite la login, nu la onComplete
    auth.reauthReason = reason || null;
    auth.isChecked = false;
    Object.defineProperty(bwState, 'bwResume', {
        value: {fp: slotFingerprint(bwState), notes: notes || ''},
        enumerable: false,
        configurable: true,
        writable: true,
    });
}

/** Consuma reluarea (o singura data): {notes, submit} sau null. */
export function takeResume(bwState) {
    const r = bwState && bwState.bwResume;
    if (!r) return null;
    delete bwState.bwResume;
    return {notes: r.notes, submit: r.fp === slotFingerprint(bwState)};
}

export const isReauthReason = (err) => err?.status === 419
    && (err?.data?.reason === 'reauth_required' || err?.data?.reason === 'login_required');

/**
 * validationCode: un cod gresit da 404 cu corp gol (`[]`); cod corect dar niciun cont cu acel
 * email + telefon da 404 {message: 'User not found.'}. Doar al doilea justifica reverificarea fara email.
 */
export const isCodeOkUserNotFound = (err) => err?.status === 404 && isObj(err?.data) && !!err.data.message;
